import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { callFunction } from '@/lib/api';
import type { Database } from '@/types/database.generated';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ReferralStatus = Database['public']['Enums']['referral_status'];
type ReferralRow = Database['public']['Tables']['referrals']['Row'];

export interface PPRDocuments {
  institution_name?: string;
  wisc_v_forside?: boolean;
  wisc_v_rapport?: boolean;
  wppsi_iv_forside?: boolean;
  wppsi_iv_rapport?: boolean;
  andet?: boolean;
  andet_items?: string[];
}

export interface HospitalDocuments {
  institution_name?: string;
  journaludskrift?: boolean;
  scannings_svar?: boolean;
  scannings_svar_mr?: boolean;
  scannings_svar_ct?: boolean;
  scannings_svar_andet?: boolean;
  scannings_svar_andet_items?: string[];
  andet?: boolean;
  andet_items?: string[];
}

export interface OtherDocuments {
  requested_items?: string;
}

export interface PaperAnamneseForms {
  smaaboern?: boolean;
  smaaboern_genhenvisning?: boolean;
  skoleboern?: boolean;
  skoleboern_genhenvisning?: boolean;
  unge_15_plus?: boolean;
  unge_15_plus_genhenvisning?: boolean;
  tillaeg_til_anamnese_13_18?: boolean;
  nordisk_sporgeskema_2_5?: boolean;
  dupaul_foraeldre_skole?: boolean;
}

export interface SchoolCompletionForms {
  førskole?: boolean;
  indskoling?: boolean;
  mellemtrin?: boolean;
  udskoling?: boolean;
}

export interface DocumentsToSend {
  anamnese?: {
    paper?: boolean;
    electronic?: boolean;
  };
  consent_forms?: {
    smaaboern?: boolean;
    smaaboern_genhenvisning?: boolean;
    skoleboern?: boolean;
    skoleboern_genhenvisning?: boolean;
    unge_15_plus?: boolean;
    unge_genhenvisning?: boolean;
    tillaegs_til_anamnese_13_18?: boolean;
    nordisk_sporgeskema?: boolean;
    dupaul_førskole_skole?: boolean;
  };
  completion_scheme?: {
    førskole?: boolean;
    indskoling?: boolean;
    mellemtrin?: boolean;
    udskoling?: boolean;
  };
}

export interface AssessmentSchemes {
  abas_3_førskole_foraeldre_paedagog?: boolean;
  abas_3_skole_foraeldre_laerer?: boolean;
  abas_3_voksen_selv_paaroerende?: boolean;
  brief_2_førskole_foraeldre_paedagog?: boolean;
  brief_2_skole_foraeldre_laerer?: boolean;
  brief_2_voksen_selv_paaroerende?: boolean;
  brief_selv?: boolean;
  brief_srs_multi_besvarelse?: boolean;
  srs_førskole_foraeldre_laerer?: boolean;
  srs_skole_foraeldre_laerer?: boolean;
  vineland_førskole?: boolean;
  vineland_skole?: boolean;
  andet?: boolean;
  andet_items?: string[];
}

export interface ReferralListItem {
  id: string;
  clinic_id: string;
  referrer_name: string | null;
  referrer_type: string | null;
  referrer_reference: string | null;
  first_name: string;
  last_name: string;
  phone: string | null;
  email: string | null;
  reason: string | null;
  notes: string | null;
  received_at: string;
  status: ReferralStatus;
  patient_id: string | null;
  assigned_clinician_id: string | null;
  has_cpr: boolean;
  consent_collected: boolean;
  info_letter_sent: boolean;
  agreement_made: boolean;
  payment_arranged: boolean;
  secretary_name: string | null;
  psychologist_name: string | null;
  id_number: string | null;
  case_number: string | null;
  ppr_documents: PPRDocuments;
  hospital_documents: HospitalDocuments;
  other_documents: OtherDocuments;
  documents_to_send: DocumentsToSend;
  assessment_schemes: AssessmentSchemes;
  paper_anamnese_forms: PaperAnamneseForms;
  school_completion_forms: SchoolCompletionForms;
  created_at: string;
  updated_at: string;
}

export interface Referral extends ReferralListItem {
  created_by_user_id: string | null;
}

export type ReferralAssignmentFilter = 'all' | 'assigned' | 'unassigned';

export interface ReferralListFilters {
  status?: ReferralStatus | 'all';
  assignment?: ReferralAssignmentFilter;
  search?: string;
}

// ── Query keys ────────────────────────────────────────────────────────────────

export const referralsListQueryKey = (clinicId: string | null, filters: ReferralListFilters) =>
  ['referrals', 'list', clinicId, filters] as const;

export const referralDetailQueryKey = (referralId: string | null | undefined) =>
  ['referrals', 'detail', referralId] as const;

export const referralIdByPatientQueryKey = (patientId: string | null | undefined) =>
  ['referrals', 'id-by-patient', patientId] as const;

// ── useReferralsList ──────────────────────────────────────────────────────────

export function useReferralsList(
  clinicId: string | null | undefined,
  filters: ReferralListFilters,
) {
  return useQuery({
    queryKey: referralsListQueryKey(clinicId ?? null, filters),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ReferralListItem[]> => {
      if (!clinicId) return [];

      let q = supabase
        .from('referrals')
        .select(
          `id, clinic_id, referrer_name, referrer_type, referrer_reference,
           first_name, last_name, phone, email, reason, notes, received_at, status,
           patient_id, assigned_clinician_id, encrypted_cpr,
           consent_collected, info_letter_sent, agreement_made, payment_arranged,
           secretary_name, psychologist_name, id_number, case_number,
           ppr_documents, hospital_documents, other_documents, documents_to_send, assessment_schemes,
           paper_anamnese_forms, school_completion_forms,
           created_at, updated_at`,
        )
        .eq('clinic_id', clinicId)
        .order('received_at', { ascending: false })
        .limit(500);

      if (filters.status && filters.status !== 'all') {
        q = q.eq('status', filters.status);
      }

      if (filters.assignment === 'unassigned') {
        q = q.is('assigned_clinician_id', null);
      } else if (filters.assignment === 'assigned') {
        q = q.not('assigned_clinician_id', 'is', null);
      }

      const search = filters.search?.trim();
      if (search) {
        q = q.or(
          `first_name.ilike.%${search}%,last_name.ilike.%${search}%,referrer_name.ilike.%${search}%`,
        );
      }

      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map(
        (row): ReferralListItem => ({
          id: row.id,
          clinic_id: row.clinic_id,
          referrer_name: row.referrer_name,
          referrer_type: row.referrer_type,
          referrer_reference: row.referrer_reference,
          first_name: row.first_name,
          last_name: row.last_name,
          phone: row.phone,
          email: row.email,
          reason: row.reason,
          notes: row.notes,
          received_at: row.received_at,
          status: row.status as ReferralStatus,
          patient_id: row.patient_id,
          assigned_clinician_id: row.assigned_clinician_id,
          has_cpr: row.encrypted_cpr !== null && row.encrypted_cpr !== undefined,
          consent_collected: row.consent_collected,
          info_letter_sent: row.info_letter_sent,
          agreement_made: row.agreement_made,
          payment_arranged: row.payment_arranged,
          secretary_name: row.secretary_name,
          psychologist_name: row.psychologist_name,
          id_number: row.id_number,
          case_number: row.case_number,
          ppr_documents: (row.ppr_documents as PPRDocuments) ?? {},
          hospital_documents: (row.hospital_documents as HospitalDocuments) ?? {},
          other_documents: (row.other_documents as OtherDocuments) ?? {},
          documents_to_send: (row.documents_to_send as DocumentsToSend) ?? {},
          assessment_schemes: (row.assessment_schemes as AssessmentSchemes) ?? {},
          paper_anamnese_forms: (row.paper_anamnese_forms as PaperAnamneseForms) ?? {},
          school_completion_forms: (row.school_completion_forms as SchoolCompletionForms) ?? {},
          created_at: row.created_at,
          updated_at: row.updated_at,
        }),
      );
    },
    staleTime: 15_000,
  });
}

// ── useReferral ───────────────────────────────────────────────────────────────

export function useReferral(referralId: string | null | undefined) {
  return useQuery({
    queryKey: referralDetailQueryKey(referralId ?? null),
    enabled: Boolean(referralId),
    queryFn: async (): Promise<Referral> => {
      if (!referralId) throw new Error('No referral id');
      const { data, error } = await supabase
        .from('referrals')
        .select('*')
        .eq('id', referralId)
        .single();
      if (error) throw error;
      return {
        ...(data as ReferralRow),
        has_cpr: (data as ReferralRow).encrypted_cpr !== null,
      } as Referral;
    },
    staleTime: 5_000,
  });
}

/** Resolves the most recent referral id linked to a patient (after accept). */
export function useReferralIdByPatient(patientId: string | null | undefined) {
  return useQuery({
    queryKey: referralIdByPatientQueryKey(patientId ?? null),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<string | null> => {
      if (!patientId) return null;
      const { data, error } = await supabase
        .from('referrals')
        .select('id')
        .eq('patient_id', patientId)
        .order('received_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data?.id ?? null;
    },
    staleTime: 5_000,
  });
}

// ── useCreateReferral ─────────────────────────────────────────────────────────

export interface CreateReferralInput extends Record<string, unknown> {
  clinic_id: string;
  referrer_name?: string;
  referrer_type?: string;
  referrer_reference?: string;
  first_name: string;
  last_name: string;
  cpr?: string;
  phone?: string;
  email?: string;
  reason?: string;
  notes?: string;
  secretary_name?: string;
  psychologist_name?: string;
  id_number?: string;
  case_number?: string;
  ppr_documents?: PPRDocuments;
  hospital_documents?: HospitalDocuments;
  other_documents?: OtherDocuments;
  documents_to_send?: DocumentsToSend;
  assessment_schemes?: AssessmentSchemes;
  paper_anamnese_forms?: PaperAnamneseForms;
  school_completion_forms?: SchoolCompletionForms;
}

export interface CreateReferralOutput {
  id: string;
  clinic_id: string;
  first_name: string;
  last_name: string;
  has_cpr: boolean;
  status: ReferralStatus;
  received_at: string;
}

export function useCreateReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateReferralInput): Promise<CreateReferralOutput> => {
      return await callFunction<CreateReferralInput, CreateReferralOutput>(
        'create-referral',
        input,
      );
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['referrals', 'list', row.clinic_id] });
    },
  });
}

// ── useAcceptReferral ─────────────────────────────────────────────────────────

export interface AcceptReferralInput extends Record<string, unknown> {
  referral_id: string;
}

export interface AcceptReferralOutput {
  referral_id: string;
  patient_id: string;
}

export function useAcceptReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AcceptReferralInput): Promise<AcceptReferralOutput> => {
      return await callFunction<AcceptReferralInput, AcceptReferralOutput>(
        'accept-referral',
        input,
      );
    },
    onSuccess: (result, vars) => {
      qc.invalidateQueries({ queryKey: referralDetailQueryKey(vars.referral_id) });
      qc.invalidateQueries({ queryKey: ['referrals', 'list'] });
      qc.invalidateQueries({ queryKey: ['patients', 'list'] });
      qc.invalidateQueries({ queryKey: referralIdByPatientQueryKey(result.patient_id) });
    },
  });
}

// ── useUpdateReferral ─────────────────────────────────────────────────────────

export interface UpdateReferralInput extends Record<string, unknown> {
  referral_id: string;
  referrer_name?: string;
  referrer_type?: string;
  referrer_reference?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  reason?: string;
  notes?: string;
  cpr?: string;
  consent_collected?: boolean;
  info_letter_sent?: boolean;
  agreement_made?: boolean;
  payment_arranged?: boolean;
  status?: ReferralStatus;
  secretary_name?: string;
  psychologist_name?: string;
  id_number?: string;
  case_number?: string;
  ppr_documents?: PPRDocuments;
  hospital_documents?: HospitalDocuments;
  other_documents?: OtherDocuments;
  documents_to_send?: DocumentsToSend;
  assessment_schemes?: AssessmentSchemes;
  paper_anamnese_forms?: PaperAnamneseForms;
  school_completion_forms?: SchoolCompletionForms;
}

export interface UpdateReferralOutput {
  id: string;
  clinic_id: string;
  referrer_name: string | null;
  referrer_type: string | null;
  referrer_reference: string | null;
  first_name: string;
  last_name: string;
  has_cpr: boolean;
  phone: string | null;
  email: string | null;
  reason: string | null;
  notes: string | null;
  received_at: string;
  status: ReferralStatus;
  patient_id: string | null;
  assigned_clinician_id: string | null;
  consent_collected: boolean;
  info_letter_sent: boolean;
  agreement_made: boolean;
  payment_arranged: boolean;
  secretary_name: string | null;
  psychologist_name: string | null;
  id_number: string | null;
  case_number: string | null;
  ppr_documents: PPRDocuments;
  hospital_documents: HospitalDocuments;
  other_documents: OtherDocuments;
  documents_to_send: DocumentsToSend;
  assessment_schemes: AssessmentSchemes;
  paper_anamnese_forms: PaperAnamneseForms;
  school_completion_forms: SchoolCompletionForms;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export function useUpdateReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateReferralInput): Promise<UpdateReferralOutput> => {
      console.log('[useUpdateReferral] Sending:', input);
      const result = await callFunction<UpdateReferralInput, UpdateReferralOutput>(
        'update-referral',
        input,
      );
      console.log('[useUpdateReferral] Received:', result);
      return result;
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await qc.cancelQueries({ queryKey: referralDetailQueryKey(variables.referral_id) });
      
      // Snapshot previous value
      const previousReferral = qc.getQueryData<Referral>(referralDetailQueryKey(variables.referral_id));
      
      // Optimistically update cache with proper merging
      qc.setQueryData<Referral>(referralDetailQueryKey(variables.referral_id), (old) => {
        if (!old) return old;
        
        // Remove referral_id from variables (it's not part of Referral type)
        const { referral_id, ...updateFields } = variables;
        
        // Merge update fields into old, preserving id
        return {
          ...old,
          ...updateFields,
          id: old.id,
        } as Referral;
      });
      
      return { previousReferral };
    },
    onError: (_err, variables, context: any) => {
      // Rollback on error
      if (context?.previousReferral) {
        qc.setQueryData(referralDetailQueryKey(variables.referral_id), context.previousReferral);
      }
    },
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: ['referrals', 'list'] });
      qc.invalidateQueries({ queryKey: referralDetailQueryKey(vars.referral_id) });
    },
  });
}

// ── useAssignReferral ─────────────────────────────────────────────────────────

export interface AssignReferralInput extends Record<string, unknown> {
  referral_id: string;
  clinician_id: string | null;
}

export interface AssignReferralOutput {
  id: string;
  clinic_id: string;
  assigned_clinician_id: string | null;
}

export function useAssignReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AssignReferralInput): Promise<AssignReferralOutput> => {
      return await callFunction<AssignReferralInput, AssignReferralOutput>(
        'assign-referral',
        input,
      );
    },
    onSuccess: (row, vars) => {
      qc.invalidateQueries({ queryKey: ['referrals', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: referralDetailQueryKey(vars.referral_id) });
    },
  });
}
